// WebsiteStep.tsx — onboarding step 1 (spec D6, handoff §6 step "Website"). Bot name + site
// URL only; on submit chains `POST /api/bots` -> `POST /api/bots/:id/sources {type:'url'}`
// (existing APIs — no new endpoints per M5 task-5 scope) and hands the new botId back up to
// `Onboarding.tsx`, which pushes `?step=2&botId=`. URL validation mirrors BotSources' URL
// tab (a bare non-empty check) plus a client-side `new URL()` parse so a missing scheme
// ("acme.vn") is corrected to "https://acme.vn" before it ever reaches the server, instead
// of round-tripping a 400 for something this easy to fix locally — `normalizeUrl` (shared
// with step 2's `CrawlStep`, see `lib/normalize-url.ts`) does the parsing. `resolveBotId`
// (below) makes that chain retry-safe: a retry after the source POST fails reuses the
// already-created bot instead of creating a duplicate, and a free-plan PLAN_LIMIT error
// adopts the workspace's existing sourceless bot rather than dead-ending the visitor.
import { useState } from 'react';
import { Button } from '@repo/ui/components/button';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import type { BotDto, SourceDto } from '@repo/shared-types';
import { apiFetch, ApiError } from '../../lib/api';
import { normalizeUrl } from '../../lib/normalize-url';
import { useT } from '../../locales/context';

export function WebsiteStep({ onCreated }: { onCreated: (botId: string) => void }) {
  const t = useT();
  const [botName, setBotName] = useState('');
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Set once a bot id is resolved (created fresh, or adopted — see `resolveBotId` below) —
  // a retry after the SUBSEQUENT `POST /sources` call fails (e.g. a network blip) must reuse
  // this bot instead of creating a second one every time the visitor clicks submit again.
  const [createdBotId, setCreatedBotId] = useState<string | null>(null);

  /** Resolves the bot to add the source to: reuses `createdBotId` from a previous attempt in
   * this same session, otherwise creates a new bot. On a free-plan `422 PLAN_LIMIT` (the
   * workspace already has its one allowed bot), adopts that existing bot INSTEAD of
   * dead-ending the visitor — but only when it's the workspace's only bot AND it has zero
   * sources yet, i.e. it looks like an abandoned/failed previous onboarding attempt rather
   * than a real second bot the visitor deliberately created elsewhere. */
  async function resolveBotId(name: string): Promise<string> {
    if (createdBotId) return createdBotId;
    try {
      const bot = await apiFetch<BotDto>('/api/bots', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      setCreatedBotId(bot.id);
      return bot.id;
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.code === 'PLAN_LIMIT') {
        const bots = await apiFetch<BotDto[]>('/api/bots');
        if (bots.length === 1) {
          const sources = await apiFetch<SourceDto[]>(`/api/bots/${bots[0]!.id}/sources`);
          if (sources.length === 0) {
            setCreatedBotId(bots[0]!.id);
            return bots[0]!.id;
          }
        }
      }
      throw err;
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    setUrlError(null);

    const name = botName.trim();
    if (!name) {
      setFormError(t.onboarding.botNameRequired);
      return;
    }
    const normalized = normalizeUrl(url);
    if (!normalized) {
      setUrlError(t.onboarding.invalidWebsiteUrl);
      return;
    }

    setSubmitting(true);
    try {
      const botId = await resolveBotId(name);
      await apiFetch<SourceDto>(`/api/bots/${botId}/sources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'url', uri: normalized }),
      });
      onCreated(botId);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : t.onboarding.genericFallbackError);
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-5 rounded-[14px] border bg-card p-8 shadow-el-1">
      <div className="flex flex-col gap-1.5">
        <h1 className="font-display text-[26px] font-bold tracking-[-0.01em]">
          {t.onboarding.websiteHeading}
        </h1>
        <p className="text-sm text-pretty text-muted-foreground">{t.onboarding.websiteSubtitle}</p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ob-bot-name">{t.onboarding.botNameLabel}</Label>
          <Input
            id="ob-bot-name"
            value={botName}
            onChange={(e) => setBotName(e.target.value)}
            placeholder={t.onboarding.botNamePlaceholder}
            maxLength={80}
            autoFocus
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ob-url">{t.onboarding.websiteUrlLabel}</Label>
          {/* `type="text"`, not `"url"` — this is the first field in the product and it was
              rejecting the most likely thing a stranger types. The form has no `noValidate`,
              so native constraint validation ran first and blocked "acme.vn" before submit,
              even though `normalizeUrl` (imported above) exists precisely to accept a bare
              hostname — its own doc calls that "the same forgiving behavior a visitor typing
              a URL by hand expects". The helper was unreachable for exactly the input it was
              written to forgive. `inputMode="url"` keeps the right mobile keyboard without
              re-introducing the gate. Same reasoning and same fix as `CrawlStep`'s
              replace-URL field. */}
          <Input
            id="ob-url"
            type="text"
            inputMode="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t.onboarding.websiteUrlPlaceholder}
            aria-invalid={!!urlError}
          />
          {urlError ? (
            <span className="text-xs text-destructive">{urlError}</span>
          ) : (
            <span className="text-xs text-muted-foreground">{t.onboarding.websiteUrlHelper}</span>
          )}
        </div>

        {formError && <p className="text-sm text-destructive">{formError}</p>}

        <Button type="submit" disabled={submitting} className="h-[42px] w-full">
          {submitting ? t.onboarding.websiteStarting : t.onboarding.websiteSubmitCta}
        </Button>
      </form>
    </div>
  );
}
